/**
 * The recommendation engine — "what should I do next?"
 *
 * This is the query that makes the concept graph worth building. The answer is
 * assembled from four candidate sources, in the order a good teacher would
 * consider them:
 *
 *   1. Repair    — a prerequisite is weak and is blocking current work.
 *   2. Review    — something mastered is fading and is due back.
 *   3. Continue  — finish the concept already in progress.
 *   4. Advance   — start the next unlocked concept on the frontier.
 *
 * Repair outranks everything: there is no point practising fractions harder
 * when the real problem is multiplication.
 */

import { allPrerequisites, frontier, isUnlocked, missingPrerequisites, type CurriculumRegistry } from '@yuan-math/curriculum';
import {
  computeMastery,
  isPlacedAndUncontradicted,
  isVerifiedByPlacement,
  knownSet,
  MASTERY_THRESHOLD,
  type ConceptState,
} from './mastery.ts';
import { dueConcepts } from './review.ts';
import type { LearnerProfile } from './types.ts';

export type RecommendationKind = 'repair' | 'review' | 'continue' | 'advance';

export type Recommendation = {
  kind: RecommendationKind;
  conceptId: string;
  /** The lesson to open, when one exists for the concept. */
  lessonId?: string;
  /** Student-facing sentence explaining why this is next. */
  reason: string;
  /** Ranking score, higher first. Exposed for debugging and tests. */
  priority: number;
};

export type RecommendationContext = {
  registry: CurriculumRegistry;
  profile: LearnerProfile;
  now: number;
};

/** Weakness worth repairing, but not so weak the student never met it. */
const REPAIR_CEILING = 0.7;

/**
 * How many "fix a foundation" items may appear at once.
 *
 * Repair legitimately outranks everything else, and the ranking still reflects
 * that — the top item is a repair whenever one exists. But a student opening
 * their home page to four consecutive repair cards learns only that they are
 * behind, which is both discouraging and useless: they can only work on one
 * thing at a time anyway. Two is enough to make the point.
 *
 * The cap is deliberately on this kind alone. Four "something new" cards carry
 * no such weight, so a shorter list there would simply be a worse list.
 */
const MAX_REPAIRS_SHOWN = 2;

export function recommendNext(context: RecommendationContext, limit = 4): Recommendation[] {
  const candidates = [
    ...repairCandidates(context),
    ...reviewCandidates(context),
    ...continueCandidates(context),
    ...advanceCandidates(context),
  ].sort((a, b) => b.priority - a.priority);

  const seen = new Set<string>();
  const out: Recommendation[] = [];
  let repairs = 0;

  for (const candidate of candidates) {
    if (out.length >= limit) break;
    if (seen.has(candidate.conceptId)) continue;
    if (candidate.kind === 'repair') {
      if (repairs >= MAX_REPAIRS_SHOWN) continue;
      repairs += 1;
    }
    seen.add(candidate.conceptId);
    out.push(candidate);
  }
  return out;
}

export function recommendOne(context: RecommendationContext): Recommendation | undefined {
  return recommendNext(context, 1)[0];
}

/* -------------------------------------------------------------------------- */
/* Candidate sources                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A prerequisite of something the student is currently working on, which is
 * itself shaky. This is the platform's core promise: when a student struggles,
 * look *underneath* the struggle.
 */
function repairCandidates(context: RecommendationContext): Recommendation[] {
  const { registry, profile, now } = context;
  const out: Recommendation[] = [];
  const inProgress = Object.values(profile.concepts).filter(isStruggling);

  for (const state of inProgress) {
    const mastery = computeMastery(state, now);
    if (mastery.overall >= REPAIR_CEILING) continue;

    for (const prereqId of allPrerequisites(registry.graph, state.conceptId)) {
      const prereq = registry.concept(prereqId);
      if (!prereq || prereq.status !== 'available') continue;
      /*
       * A prerequisite the placement test asked about directly, and got a
       * correct answer to, is not the explanation for a struggle further up —
       * and offering it reads as "you are behind" to a student who is not.
       *
       * A prerequisite the test merely *inferred* is a different matter. It was
       * never asked, and the concept resting on it is now going wrong, which is
       * precisely the evidence that an inference can be wrong. Those stay
       * eligible, or a strong placement would permanently hide the foundations
       * of everything the student later struggles with.
       */
      const prereqState = profile.concepts[prereqId];
      if (isVerifiedByPlacement(prereqState)) continue;
      const prereqMastery = computeMastery(prereqState, now);
      if (prereqMastery.overall >= REPAIR_CEILING) continue;

      const blocked = registry.concept(state.conceptId)?.title ?? state.conceptId;
      out.push({
        kind: 'repair',
        conceptId: prereqId,
        ...lessonFor(registry, prereqId),
        reason: `Strengthening ${prereq.title} will make ${blocked} much easier.`,
        // The weaker the foundation, the more urgent the repair.
        priority: 100 + (REPAIR_CEILING - prereqMastery.overall) * 40,
      });
    }
  }

  // A misconception seen repeatedly names its own repair concept.
  for (const state of Object.values(profile.concepts)) {
    for (const [misconceptionId, count] of Object.entries(state.misconceptions)) {
      if (count < 2) continue;
      const target = REMEDIATION.get(misconceptionId);
      if (!target) continue;
      const concept = registry.concept(target);
      if (!concept || concept.status !== 'available') continue;
      out.push({
        kind: 'repair',
        conceptId: target,
        ...lessonFor(registry, target),
        reason: `This has come up ${count} times. A short review of ${concept.title} should clear it up.`,
        priority: 110 + count * 5,
      });
    }
  }

  return out;
}

/**
 * Has this concept actually gone badly?
 *
 * Repair exists to look underneath a struggle, so there has to *be* a struggle.
 * Low mastery on its own does not establish one: mastery starts low and climbs
 * with evidence, so a concept answered correctly once sits near 0.54 — under
 * the repair ceiling — purely because almost nothing has been measured yet.
 * Reading that as weakness told students who had got every question right that
 * their foundations needed work.
 *
 * A wrong answer, or a misconception seen more than once, is evidence. Being
 * new is not.
 *
 * Exported because the Adventure Journal asks the same question of the same
 * profile (`journalEntries` in `@yuan-math/world`). Two definitions of
 * "struggling" would let the journal write up a challenge the recommender does
 * not believe in, or stay silent about the concept the home page is sending
 * the student back to — the two screens would be describing different students.
 */
export function isStruggling(state: ConceptState | undefined): boolean {
  if (!state) return false;
  if (state.attempts === 0) return false;
  return state.attempts > state.correct || Object.values(state.misconceptions).some((count) => count >= 2);
}

const isDefined = <T,>(value: T | undefined): value is T => value !== undefined;

/** Populated by `registerRemediation` from the exercise package's catalog. */
const REMEDIATION = new Map<string, string>();

export function registerRemediation(entries: readonly { id: string; remediationConceptId: string }[]): void {
  for (const entry of entries) REMEDIATION.set(entry.id, entry.remediationConceptId);
}

function reviewCandidates(context: RecommendationContext): Recommendation[] {
  const { registry, profile, now } = context;
  return dueConcepts(profile.concepts, now)
    .map((conceptId) => {
      const concept = registry.concept(conceptId);
      if (!concept || concept.status !== 'available') return undefined;
      const state = profile.concepts[conceptId] as ConceptState;
      const overdueDays = Math.max(0, (now - (state.dueAt ?? now)) / 86_400_000);
      return {
        kind: 'review' as const,
        conceptId,
        ...lessonFor(registry, conceptId),
        reason:
          overdueDays > 3
            ? `It has been a while since you practised ${concept.title}. A quick review will keep it.`
            : `${concept.title} is due for review today.`,
        priority: 70 + Math.min(20, overdueDays * 2),
      };
    })
    .filter(isDefined);
}

/**
 * Something the student has started and not finished.
 *
 * "Started" has to mean started. A placement answer is one question, asked by
 * us, about a concept the student may have learned years ago — reading it as
 * work in progress is how a learner placed at Grade 12 was told they were "54%
 * of the way through Counting and Quantity". That number is not a description
 * of them; it is a description of how little we asked.
 */
function continueCandidates(context: RecommendationContext): Recommendation[] {
  const { registry, profile, now } = context;
  return Object.values(profile.concepts)
    .map((state) => {
      const mastery = computeMastery(state, now);
      if (mastery.overall >= MASTERY_THRESHOLD || state.attempts === 0) return undefined;
      if (isPlacedAndUncontradicted(state)) return undefined;
      const concept = registry.concept(state.conceptId);
      if (!concept || concept.status !== 'available') return undefined;
      // Only continue what the student can actually do right now.
      if (!isUnlocked(registry.graph, state.conceptId, knownSet(profile.concepts, now))) return undefined;
      return {
        kind: 'continue' as const,
        conceptId: state.conceptId,
        ...lessonFor(registry, state.conceptId),
        reason: `You are ${Math.round(mastery.overall * 100)}% of the way through ${concept.title}.`,
        // Closer to the line ranks higher: finish what is nearly done.
        priority: 50 + mastery.overall * 20,
      };
    })
    .filter(isDefined);
}

/**
 * Something new the student is ready for.
 *
 * Ranked by how close it sits to the level they are working at, which the
 * frontier alone cannot tell you. The frontier is wide — a hundred-odd concepts
 * for a student near the top of the curriculum — and it is enumerated in
 * teaching order, so taking the first few hands back the easiest concepts in
 * the graph. For a student placed at Grade 12 that meant "you are ready for
 * Telling Time": true, useless, and insulting.
 *
 * The diagnostic learned this same lesson about its own starting point; the
 * recommender was never told.
 */
function advanceCandidates(context: RecommendationContext): Recommendation[] {
  const { registry, profile, now } = context;
  const known = knownSet(profile.concepts, now);
  const started = new Set(
    Object.values(profile.concepts)
      .filter((state) => !isPlacedAndUncontradicted(state))
      .map((state) => state.conceptId),
  );
  const target = workingLevel(context);

  return frontier(registry.graph, known)
    .filter((conceptId) => !started.has(conceptId))
    .map((conceptId) => {
      const concept = registry.concept(conceptId);
      if (!concept || concept.status !== 'available') return undefined;
      const distance = Math.abs(conceptLevelOrder(registry, conceptId) - target);
      return {
        kind: 'advance' as const,
        conceptId,
        ...lessonFor(registry, conceptId),
        reason: `You are ready for ${concept.title}.`,
        // Nearest to where they are working ranks highest, and nothing drops
        // below the review band — a new concept never outranks a repair.
        priority: 40 - Math.min(8, distance),
      };
    })
    .filter(isDefined)
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 6);
}

/**
 * The level a concept is first taught at, as a number.
 *
 * Exported because `packages/world` asks the same question about the same
 * concepts, and a second copy of "what level is this" would be free to drift
 * from the one the recommendations are ranked by.
 */
export function conceptLevelOrder(registry: CurriculumRegistry, conceptId: string): number {
  const orders = (registry.concept(conceptId)?.levels ?? [])
    .map((levelId) => registry.level(levelId)?.order)
    .filter((value): value is number => value !== undefined);
  return orders.length > 0 ? Math.min(...orders) : 0;
}

/**
 * Where the student is working, as a level order.
 *
 * Their own stated level first — it is the one thing here they chose — then the
 * placement test's estimate, then the hardest thing they have actually done.
 *
 * **This is the platform's one answer to "what level is this student at".** It
 * is exported so that nothing else has to invent a second one: `advance` ranks
 * by distance from it, and `packages/world` decides which kingdom a student is
 * standing in by distance from it. A placement result reaches the adventure
 * through this function and no other.
 */
export function workingLevel(context: RecommendationContext): number {
  const { registry, profile, now } = context;

  const stated = profile.levelId ?? profile.diagnostic?.recommendedLevel;
  const statedOrder = stated ? registry.level(stated)?.order : undefined;
  if (statedOrder !== undefined) return statedOrder;

  const touched = Object.values(profile.concepts)
    .filter((state) => state.attempts > 0 || computeMastery(state, now).overall > 0)
    .map((state) => conceptLevelOrder(registry, state.conceptId));
  return touched.length > 0 ? Math.max(...touched) : 0;
}

function lessonFor(registry: CurriculumRegistry, conceptId: string): { lessonId?: string } {
  const lesson = registry.lessonsForConcept(conceptId)[0];
  return lesson ? { lessonId: lesson.id } : {};
}

/* -------------------------------------------------------------------------- */
/* Diagnosis of a specific struggle                                           */
/* -------------------------------------------------------------------------- */

export type StruggleDiagnosis = {
  conceptId: string;
  /** Prerequisites that are not yet mastered, weakest first. */
  weakPrerequisites: { conceptId: string; mastery: number }[];
  /** Misconceptions this student keeps hitting on this concept. */
  repeatedMisconceptions: { id: string; count: number }[];
  /** What to do about it, in one sentence. */
  advice: string;
};

/**
 * Explains *why* a student is stuck on one concept — the data behind the
 * "what concept is missing" step of the four-part explanation (§3).
 */
export function diagnoseStruggle(context: RecommendationContext, conceptId: string): StruggleDiagnosis {
  const { registry, profile, now } = context;
  const concept = registry.concept(conceptId);

  const weakPrerequisites = allPrerequisites(registry.graph, conceptId)
    .map((id) => ({ conceptId: id, mastery: computeMastery(profile.concepts[id], now).overall }))
    .filter((entry) => entry.mastery < REPAIR_CEILING)
    .sort((a, b) => a.mastery - b.mastery)
    .slice(0, 3);

  const state = profile.concepts[conceptId];
  const repeatedMisconceptions = Object.entries(state?.misconceptions ?? {})
    .map(([id, count]) => ({ id, count }))
    .filter((entry) => entry.count >= 2)
    .sort((a, b) => b.count - a.count);

  let advice: string;
  const weakest = weakPrerequisites[0];
  if (weakest) {
    const name = registry.concept(weakest.conceptId)?.title ?? weakest.conceptId;
    advice = `Go back to ${name} first — it is what ${concept?.title ?? 'this concept'} is built on.`;
  } else if (repeatedMisconceptions.length > 0) {
    advice = 'The prerequisites are solid, so this is a specific habit to unlearn rather than a missing foundation.';
  } else {
    advice = 'Nothing underneath is missing. More practice at this level should be enough.';
  }

  return { conceptId, weakPrerequisites, repeatedMisconceptions, advice };
}

/** Concepts blocked purely by a missing prerequisite — the "locked" state. */
export function blockedConcepts(context: RecommendationContext): { conceptId: string; missing: string[] }[] {
  const { registry, profile, now } = context;
  const known = knownSet(profile.concepts, now);
  return registry
    .availableConcepts()
    .map((concept) => ({ conceptId: concept.id, missing: missingPrerequisites(registry.graph, concept.id, known) }))
    .filter((entry) => entry.missing.length > 0);
}
